import {
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material"
import { Title } from "components/Common/Title"
import { CreateLicenseDialog } from "components/License/CreateLicenseDialog"
import { LicenseList } from "components/License/LicenseList"
import { LicensePoolAvailabilityChart } from "components/LicensePool/LicensePoolAvailabilityChart"
import { useAppDispatch, useAppSelector } from "hooks"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import authService from "services/authService"
import licensePoolService from "services/licensePoolService"
import licenseService from "services/licenseService"
import organizationService from "services/organizationService"
import userService from "services/userService"
import { setCurrentLicensePool } from "slices/licensePoolSlice"
import { setLicenses } from "slices/licenseSlice"
import { setCurrentOrganization } from "slices/organizationSlice"
import { setUsers } from "slices/usersSlice"
import { UserRoles } from "types/userRoles"

export function LicensePool() {
  const [roles, setRoles] = useState<Array<string>>()
  const { id, licensePoolId } = useParams()

  const dispatch = useAppDispatch()

  const currentOrganization = useAppSelector((state) => state.organization.currentOrganization)

  const currentLicensePool = useAppSelector((state) => state.licensepool.currentLicensePool)

  const users = useAppSelector((state) => state.users.users)
  const licenses = useAppSelector((state) => state.license.licenses)
  const licensePools = useAppSelector((state) => state.licensepool.licensePools)
  useEffect(() => {
    authService.getRolesAssignedToUser().then((roles) => {
      setRoles(roles)
    })

    if (currentOrganization === undefined && id) {
      organizationService
        .getOrganization(id)
        .then((organization) => {
          dispatch(setCurrentOrganization(organization))
        })
        .catch((err) => {
          console.log(err)
        })
    }

    if (currentLicensePool === undefined && licensePoolId) {
      licensePoolService
        .getLicensePool(licensePoolId)
        .then((licensePool) => {
          dispatch(setCurrentLicensePool(licensePool))
        })
        .catch((err) => {
          console.log(err)
        })
    } else if (currentLicensePool && currentLicensePool.id !== licensePoolId) {
      const licensePool = licensePools.find((x) => x.id === licensePoolId)

      dispatch(setCurrentLicensePool(licensePool))
    }

    if (licenses.length < 1 && id) {
      licenseService
        .getOrganizationLicenses(id)
        .then((licenses) => {
          dispatch(setLicenses(licenses))
        })
        .catch((err) => {
          console.log(err)
        })
    }

    if (users.length < 1 && id) {
      userService
        .getOrganizationUsers(id)
        .then((users) => {
          dispatch(setUsers(users))
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }, [
    currentLicensePool,
    currentOrganization,
    dispatch,
    id,
    licensePoolId,
    licensePools.find,
    licenses.length,
    users.length,
  ])

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Title>{currentLicensePool?.name.toLocaleUpperCase()}</Title>
      </Grid>
      <Grid item xs={4}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", height: 200 }}>
          <Grid>
            <Grid item>
              <Title>FREE LICENSES</Title>
            </Grid>
            <Grid container justifyContent="center">
              <Typography variant="h1">{currentLicensePool?.freeLicenses}</Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={4}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", height: 200 }}>
          <Grid>
            <Grid item>
              <Title>ACTIVE LICENSES</Title>
            </Grid>
            <Grid container justifyContent="center">
              <Typography variant="h1">{currentLicensePool?.assignedLicenses}</Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={4}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", height: 200 }}>
          <Grid>
            <Grid item>
              <Title>TRAINING MODULES</Title>
            </Grid>
            <Grid item>
              <List sx={{ height: 150, overflow: "auto" }}>
                {currentLicensePool?.trainingModules.map((module) => (
                  <ListItem key={module.id} disablePadding>
                    <ListItemButton>
                      <ListItemText primary={`${module.simulatorApplicationName}: ${module.name}`} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", height: 280 }}>
          <Grid container paddingBottom={2}>
            <Grid item xs>
              <Title>LICENSE AVAILABILITY FORECAST</Title>
            </Grid>
          </Grid>
          {currentLicensePool && (
            <LicensePoolAvailabilityChart licensePool={currentLicensePool} forecastMonths={13} />
          )}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
          <Grid container paddingBottom={2}>
            <Grid item xs>
              <Title>LICENSES</Title>
            </Grid>
            <Grid item>{roles?.includes(UserRoles.HIAB_ADMIN) && <CreateLicenseDialog />}</Grid>
          </Grid>
          {currentLicensePool && <LicenseList licensePool={currentLicensePool} />}
        </Paper>
      </Grid>
    </Grid>
  )
}
